'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, FileText, Shield, Gavel, Mail } from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CompanyHeader from '@/components/CompanyHeader';

// ИМПОРТИРУЕМ ЛОКАЛИЗАЦИЮ
import { useLanguage } from '@/components/LanguageProvider';

export default function LegalLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // ДОСТАЕМ locale
  const { t, locale } = useLanguage();
  const tl = t.legal.layout;
  
  // Добавляем /${locale} ко всем ссылкам
  const globalNav = [
    { href: `/${locale}/legal/terms`, label: tl.nav.terms, icon: FileText },
    { href: `/${locale}/legal/privacy`, label: tl.nav.privacy, icon: Shield },
    { href: `/${locale}/legal/rules`, label: tl.nav.rules, icon: Gavel },
    { href: `/${locale}/legal/contacts`, label: tl.nav.contacts, icon: Mail },
  ];

  const [toc, setToc] = useState<{ id: string; label: string }[]>([]);
  const [activeSection, setActiveSection] = useState('');
  
  const [isTocHovered, setIsTocHovered] = useState(false);
  const tocContainerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const extractToc = () => {
      const elements = Array.from(document.querySelectorAll('section[data-toc]'));
      const newToc = elements.map((el) => ({
        id: el.id,
        label: el.getAttribute('data-toc') || '',
      }));
      setToc(newToc);
      if (newToc.length > 0 && !activeSection) setActiveSection(newToc[0].id);
    };

    extractToc();
    const timeout = setTimeout(extractToc, 200);
    return () => clearTimeout(timeout);
  }, [pathname, t]);

  useEffect(() => {
    if (toc.length === 0) return;

    const handleScroll = () => {
      const elements = Array.from(document.querySelectorAll('section[data-toc]'));
      if (!elements.length) return;

      let currentId = elements[0].id;
      
      for (const el of elements) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 200) {
          currentId = el.id;
        }
      }

      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 50) {
        currentId = elements[elements.length - 1].id;
      }

      setActiveSection(currentId);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [toc]);

  useEffect(() => {
    if (!activeSection || !tocContainerRef.current || isTocHovered) return;
    
    const container = tocContainerRef.current;
    const activeElement = container.querySelector(`[data-toc-id="${activeSection}"]`) as HTMLElement;

    if (activeElement) {
      const scrollPos = activeElement.offsetTop - container.clientHeight / 2 + activeElement.clientHeight / 2;
      container.scrollTo({ top: scrollPos, behavior: 'smooth' });
    }
  }, [activeSection, isTocHovered]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative min-h-screen bg-[#111111] text-[#ffffff] selection:bg-[#667fff] selection:text-white pt-[115px] sm:pt-[88px] font-sans flex flex-col">
      <style jsx global>{`
        @font-face {
          font-family: 'MinecraftCustom';
          src: url('https://webstatic.royolands.com/upload/contentweb/minecraft.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
        .font-mc { font-family: 'MinecraftCustom', sans-serif !important; font-weight: normal !important; letter-spacing: normal !important; }
        html { scroll-behavior: smooth; }
        .legal-content p { margin-bottom: 1.25rem; line-height: 1.6; color: #aaaaaa; }
        .legal-content a { color: #667fff; text-decoration: none; font-weight: 500; }
        .legal-content a:hover { text-decoration: underline; }

        .toc-scrollbar::-webkit-scrollbar { width: 4px; }
        .toc-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .toc-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 4px; }
        .toc-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.2); }
      `}</style>

      <CompanyHeader />

      <div className="relative z-10 w-full bg-[#111111] rounded-b-[20px] md:rounded-b-[40px] border-b border-white/5 pb-20 flex-1 flex flex-col">
        <Header />

        <div className="w-full max-w-[120rem] mx-auto px-4 md:px-14 xl:px-[11.25rem] pt-12 md:pt-20 flex-1 flex flex-col lg:flex-row gap-8 lg:gap-12 items-start justify-between">
          
          {toc.length > 0 && (
            <aside 
              ref={tocContainerRef}
              data-lenis-prevent="true"
              onMouseEnter={() => setIsTocHovered(true)} 
              onMouseLeave={() => setIsTocHovered(false)} 
              className="hidden lg:block w-56 shrink-0 sticky top-32 max-h-[calc(100vh-10rem)] overflow-y-auto toc-scrollbar pr-2"
            >
              <p className="text-xs font-bold uppercase text-[#aaaaaa] tracking-widest mb-6 pl-3">{tl.toc}</p>
              <div className="flex flex-col gap-1 border-l border-white/5 ml-3">
                {toc.map((sec) => (
                  <button
                    key={sec.id}
                    data-toc-id={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className={`
                      w-full text-left px-4 py-1.5 text-[14px] transition-colors outline-none cursor-pointer -ml-[1px]
                      ${activeSection === sec.id 
                        ? 'text-white font-medium border-l-2 border-[#667fff]' 
                        : 'text-[#aaaaaa] border-l-2 border-transparent hover:text-white'}
                    `}
                  >
                    {sec.label}
                  </button>
                ))}
              </div>
            </aside>
          )}

          <main className="flex-1 w-full min-w-0 max-w-3xl">
            {children}
          </main>

          <aside className="hidden lg:block w-[300px] shrink-0 sticky top-32">
            <div className="bg-[#161616] border border-white/5 rounded-[20px] pt-6 px-5 pb-3">
              <p className="text-xs font-bold uppercase text-[#aaaaaa] tracking-widest mb-4 px-3">
                {tl.docs}
              </p>
              <nav className="flex flex-col gap-1">
                {globalNav.map((link) => {
                  // ПРОВЕРКА ТЕПЕРЬ РАБОТАЕТ
                  const isActive = pathname === link.href;
                  const Icon = link.icon;
                  return (
                    <Link 
                      key={link.href}
                      href={link.href}
                      className={`
                        flex items-center gap-3 w-full text-left px-3 py-3 text-[14px] transition-colors outline-none cursor-pointer rounded-[12px]
                        ${isActive 
                          ? 'bg-white/5 text-white font-medium' 
                          : 'text-[#aaaaaa] hover:bg-white/[0.02] hover:text-white'}
                      `}
                    >
                      <Icon size={16} className={isActive ? 'text-[#667fff]' : 'text-[#aaaaaa] shrink-0'} />
                      <span className="leading-tight">{link.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

        </div>
      </div>

      <Footer />
      <ScrollToTopButton text={tl.backToTop} />
    </div>
  );
}

function ScrollToTopButton({ text }: { text: string }) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const toggleVisibility = () => setIsVisible(window.scrollY > 500);
        window.addEventListener('scroll', toggleVisibility, { passive: true });
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.button
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} transition={{ duration: 0.2 }}
                    className="fixed bottom-8 right-8 z-50 w-12 h-12 bg-[#161616] border border-white/10 hover:bg-[#283059] text-white rounded-[12px] flex items-center justify-center cursor-pointer transition-colors outline-none"
                    onClick={scrollToTop} aria-label={text} title={text}
                >
                    <ArrowUp size={24} />
                </motion.button>
            )}
        </AnimatePresence>
    );
}